import { MARK_FOR_REVIEW, STRIKETHROUGH } from '@app/constants'
import React, { useCallback, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { useLocation } from 'react-router-dom'

export type QuestionContextMenuContextType = {
  markedItems: string[]
  strikethroughAnswers: string[]
  setMarkForReview: () => void
  toggleStrikethrough: (answerId: string) => () => void
  strikethroughEnabled: boolean
  toggleStrikethroughEnabled: () => void
  // set when status should be announced with the menu button (aria-describedby); cleared after 3 seconds
  pendingStatusAnnouncementId: string | null
}

export const QuestionContextMenuContext = React.createContext<QuestionContextMenuContextType | null>(null)

interface Props {
  itemId: string
  children?: React.ReactNode
}

// status message id for mark/unmark for review
export const MARK_FOR_REVIEW_STATUS_ID = 'question-mark-for-review-status'

const setMarkForReviewValue = (value: string) => {
  localStorage.setItem(MARK_FOR_REVIEW, value)
}

const setStrikethroughValue = (value: string) => {
  localStorage.setItem(STRIKETHROUGH, value)
}

const createKey = (ids: string[]): string => ids.join('/')

export const QuestionContextMenuProvider: React.FC<Props> = ({ itemId, children }) => {
  const search = useLocation().search
  const searchParams = new URLSearchParams(search)
  const userId = searchParams.get('userId')
  const testId = searchParams.get('id')

  const [markedItems, setMarkedItems] = useState<string[]>([])
  const [pendingStatusAnnouncementId, setPendingStatusAnnouncementId] = useState<string | null>(null)

  const [strikethroughAnswers, setStrikethroughAnswers] = useState<string[]>([])
  const [strikethroughEnabled, setStrikethroughEnabled] = useState<boolean>(false)

  useEffect(() => {
    if (!pendingStatusAnnouncementId) return
    // clear the status announcement after 3 seconds
    const timeout = window.setTimeout(() => setPendingStatusAnnouncementId(null), 3000)
    return () => window.clearTimeout(timeout)
  }, [pendingStatusAnnouncementId])

  const toggleStrikethroughEnabled = useCallback(() => {
    setStrikethroughEnabled(prev => !prev)
  }, [])

  const setMarkForReview = useCallback(() => {
    const value = JSON.parse(localStorage.getItem(MARK_FOR_REVIEW))

    const key = createKey([userId, testId])

    const markedItems: string[] = value?.[key] || []

    const newMarkedItems = markedItems?.includes(itemId)
      ? markedItems.filter(item => item !== itemId)
      : [...markedItems, itemId]

    setMarkForReviewValue(
      JSON.stringify({
        ...value,
        [key]: newMarkedItems,
      }),
    )

    flushSync(() => {
      setMarkedItems(newMarkedItems)
      setPendingStatusAnnouncementId(MARK_FOR_REVIEW_STATUS_ID)
    })
  }, [userId, testId, itemId])

  const toggleStrikethrough = useCallback(
    (answerId: string) => () => {
      if (!answerId) return

      const value = JSON.parse(localStorage.getItem(STRIKETHROUGH))

      const key = createKey([userId, testId, itemId])

      const strikethroughAnswers: string[] = value?.[key] || []

      const newStrikethroughAnswers = strikethroughAnswers?.includes(answerId)
        ? strikethroughAnswers.filter(answer => answer !== answerId)
        : [...strikethroughAnswers, answerId]

      setStrikethroughValue(
        JSON.stringify({
          ...value,
          [key]: newStrikethroughAnswers,
        }),
      )

      setStrikethroughAnswers(newStrikethroughAnswers)
    },
    [userId, testId, itemId],
  )

  useEffect(() => {
    const markedItems = JSON.parse(localStorage.getItem(MARK_FOR_REVIEW))
    const strikethroughAnswers = JSON.parse(localStorage.getItem(STRIKETHROUGH))

    const markedItemsKey = createKey([userId, testId])
    const strikethroughAnswersKey = createKey([userId, testId, itemId])

    setMarkedItems(markedItems?.[markedItemsKey] || [])
    setStrikethroughAnswers(strikethroughAnswers?.[strikethroughAnswersKey] || [])
  }, [userId, testId, itemId])

  return (
    <QuestionContextMenuContext.Provider
      value={{
        markedItems,
        strikethroughAnswers,
        setMarkForReview,
        pendingStatusAnnouncementId,
        toggleStrikethrough,
        strikethroughEnabled,
        toggleStrikethroughEnabled,
      }}
    >
      {children}
    </QuestionContextMenuContext.Provider>
  )
}
